/**
 * useDataTablePersistence Hook
 *
 * Reads and writes DataTable UI preferences (column visibility + filter chip
 * selections) to localStorage, namespaced per table by a caller-supplied key.
 *
 * Scope is deliberately per-browser, not per-user: these are UI preferences,
 * carry no PII, and never round-trip to the backend. Session cleanup drops the saved
 * filter chips (they can hide another roaster's rows) but keeps column visibility.
 *
 * Every storage access is guarded and wrapped in try/catch — a missing `window`
 * (SSR), malformed JSON, a non-object payload or a throwing `localStorage`
 * (Safari private mode, quota exceeded) all degrade to "no persisted state"
 * rather than crashing the table.
 */

import * as React from "react";

/** A filter chip selection stored in a locale-independent form. */
export interface DataTableFilterSelection {
  column: string;
  value: string;
}

export interface DataTablePersistedState {
  columnVisibility?: Record<string, boolean>;
  filters?: DataTableFilterSelection[];
}

export type DataTablePersistedSection = "columnVisibility" | "filters";

export interface UseDataTablePersistenceOptions {
  /** Namespaced storage key, e.g. "products-table". Omit to disable persistence entirely. */
  persistenceKey?: string;
  /** Persist the column visibility map. Defaults to true. */
  persistColumnVisibility?: boolean;
  /** Persist filter chip selections. Defaults to true. */
  persistFilters?: boolean;
}

export interface UseDataTablePersistenceResult {
  /** State read once, lazily, on mount. Empty when persistence is off or unreadable. */
  restored: DataTablePersistedState;
  /** Merge the given sections into the stored entry. Sections that are opted out are ignored. */
  persist: (next: DataTablePersistedState) => void;
  /**
   * Remove one section, or the whole entry when called with no argument. A section that is
   * opted out is left untouched, mirroring `persist`.
   */
  clear: (section?: DataTablePersistedSection) => void;
}

const STORAGE_KEY_PREFIX = "bcc-datatable-";

/**
 * Every failure here is non-fatal by design — the table keeps working, the user just loses a UI
 * preference. `console.warn` rather than `console.error` for that reason, but it is never
 * swallowed silently: a quota-exceeded write or a corrupted payload should be visible in the
 * console when someone reports "my hidden columns keep coming back".
 */
const warnPersistenceFailure = (message: string, error: unknown) => {
  console.warn(`[DataTable persistence] ${message}`, error);
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

const parseColumnVisibility = (value: unknown): Record<string, boolean> | undefined => {
  if (!isRecord(value)) return undefined;
  return Object.entries(value).reduce<Record<string, boolean>>((visibility, [columnId, isVisible]) => {
    if (typeof isVisible === "boolean") {
      visibility[columnId] = isVisible;
    }
    return visibility;
  }, {});
};

const parseFilters = (value: unknown): DataTableFilterSelection[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const entries: readonly unknown[] = value;
  return entries.reduce<DataTableFilterSelection[]>((selections, entry) => {
    if (isRecord(entry) && typeof entry.column === "string" && typeof entry.value === "string") {
      selections.push({ column: entry.column, value: entry.value });
    }
    return selections;
  }, []);
};

/** Narrows an arbitrary JSON payload to the persisted shape; anything unrecognised is dropped. */
const parsePersistedState = (raw: string | null): DataTablePersistedState => {
  if (raw === null) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return {};
    const columnVisibility = parseColumnVisibility(parsed.columnVisibility);
    const filters = parseFilters(parsed.filters);
    return {
      ...(columnVisibility ? { columnVisibility } : {}),
      ...(filters ? { filters } : {}),
    };
  } catch (error) {
    warnPersistenceFailure("Ignoring malformed stored state.", error);
    return {};
  }
};

const getStorage = (): Storage | undefined => {
  if (globalThis.window === undefined) return undefined;
  try {
    return globalThis.window.localStorage;
  } catch (error) {
    warnPersistenceFailure("localStorage is unavailable; preferences will not persist.", error);
    return undefined;
  }
};

const readStoredState = (storageKey: string): DataTablePersistedState => {
  const storage = getStorage();
  if (!storage) return {};
  try {
    return parsePersistedState(storage.getItem(storageKey));
  } catch (error) {
    warnPersistenceFailure(`Could not read "${storageKey}".`, error);
    return {};
  }
};

/** Removes the saved filter chips from every persisted table, leaving other preferences such as column visibility. */
export const clearAllPersistedDataTableFilters = (): void => {
  const storage = getStorage();
  if (!storage) return;
  try {
    const storageKeys = Array.from({ length: storage.length }, (_, index) => storage.key(index)).filter(
      (key): key is string => key?.startsWith(STORAGE_KEY_PREFIX) === true
    );
    storageKeys.forEach((storageKey) => {
      const { filters: _filters, ...remaining } = parsePersistedState(storage.getItem(storageKey));
      if (Object.keys(remaining).length === 0) {
        storage.removeItem(storageKey);
        return;
      }
      storage.setItem(storageKey, JSON.stringify(remaining));
    });
  } catch (error) {
    warnPersistenceFailure("Could not clear persisted filters.", error);
  }
};

export const useDataTablePersistence = ({
  persistenceKey,
  persistColumnVisibility = true,
  persistFilters = true,
}: UseDataTablePersistenceOptions): UseDataTablePersistenceResult => {
  // Read once, lazily — subsequent renders must not re-hit storage.
  const [restored] = React.useState<DataTablePersistedState>(() => {
    if (persistenceKey === undefined) return {};
    const stored = readStoredState(`${STORAGE_KEY_PREFIX}${persistenceKey}`);
    return {
      ...(persistColumnVisibility && stored.columnVisibility ? { columnVisibility: stored.columnVisibility } : {}),
      ...(persistFilters && stored.filters ? { filters: stored.filters } : {}),
    };
  });

  const persist = React.useCallback(
    (next: DataTablePersistedState) => {
      // Key first: the prop contract promises no storage access at all without one, and in a
      // restricted iframe merely reading the `localStorage` getter throws and would warn.
      if (persistenceKey === undefined) return;
      const storage = getStorage();
      if (!storage) return;

      const allowed: DataTablePersistedState = {
        ...(persistColumnVisibility && next.columnVisibility !== undefined ? { columnVisibility: next.columnVisibility } : {}),
        ...(persistFilters && next.filters !== undefined ? { filters: next.filters } : {}),
      };
      if (Object.keys(allowed).length === 0) return;

      const storageKey = `${STORAGE_KEY_PREFIX}${persistenceKey}`;
      try {
        // Merge so the two halves never clobber each other.
        const merged: DataTablePersistedState = { ...parsePersistedState(storage.getItem(storageKey)), ...allowed };
        storage.setItem(storageKey, JSON.stringify(merged));
      } catch (error) {
        // Storage unavailable or full — preferences simply don't survive this session.
        warnPersistenceFailure(`Could not write "${storageKey}".`, error);
      }
    },
    [persistenceKey, persistColumnVisibility, persistFilters]
  );

  const clear = React.useCallback(
    (section?: DataTablePersistedSection) => {
      if (persistenceKey === undefined) return;
      // Clearing has to honour the same opt-outs as writing, or a table mounted with
      // `persistFilters={false}` would delete a preference another mount had saved under this key.
      if (section === "columnVisibility" && !persistColumnVisibility) return;
      if (section === "filters" && !persistFilters) return;
      const storage = getStorage();
      if (!storage) return;

      const storageKey = `${STORAGE_KEY_PREFIX}${persistenceKey}`;
      try {
        if (section === undefined) {
          storage.removeItem(storageKey);
          return;
        }
        const remaining = parsePersistedState(storage.getItem(storageKey));
        delete remaining[section];
        if (Object.keys(remaining).length === 0) {
          storage.removeItem(storageKey);
          return;
        }
        storage.setItem(storageKey, JSON.stringify(remaining));
      } catch (error) {
        // Storage unavailable — nothing to clear.
        warnPersistenceFailure(`Could not clear "${storageKey}".`, error);
      }
    },
    [persistenceKey, persistColumnVisibility, persistFilters]
  );

  return React.useMemo(() => ({ restored, persist, clear }), [restored, persist, clear]);
};
